import { NavigateFunction } from "react-router-dom";
import { AxiosError, AxiosResponse } from "axios";
import { createApi } from "../../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";
import { FLOW_WINDOW_SECONDS } from "../vpnDebugConstants";
import { VpnDebugEnqueueResponse } from "../vpnDebugInterfaces";
import { useVpnDebuggerStore } from "../vpnDebuggerStore/vpnDebuggerStore";
import { extractVpnDebugApiError } from "./vpnDebugApiError";
import { mergeVpnDebugResult } from "./runVpnDebugLadder";
import { pollVpnDebugTask } from "./pollVpnDebugTask";

/**
 * Watch the gateway's packet-arrival counters for a fixed window.
 *
 * A third plane, separate from the tunnel and device planes and never run
 * automatically. The counters are nftables per-source packet counts the
 * WireGuard reconciler provisions, read *before* any crypto is applied — which
 * is the point: WireGuard drops anything failing key validation before it can
 * be attributed to a peer, so "wrong key" and "nothing arrived" look identical
 * from `wg show` alone. These counters are what separates them.
 *
 * Operator-triggered because it costs a real 30s, and the useful way to spend
 * it is while the customer restarts their tunnel.
 *
 * `tunnelTaskId` matters: the server reads that run's tunnel evidence to
 * recover the peer's last-known endpoint address. Without it there is nothing
 * to attribute arrivals to and the step reports itself inconclusive rather
 * than claiming silence.
 */
export const runVpnFlowWindow = async (
  tunnelId: number,
  tunnelTaskId: string,
  navigate: NavigateFunction,
  retryFunction: () => void
): Promise<void> => {
  const store = useVpnDebuggerStore.getState();
  const token = store.runToken;
  const startedAt = Date.now();

  store.setFlowOffered(false);
  store.setRunning(true);
  store.setStage("Watching the wire on the gateway…");
  store.setPendingSteps(["tunnel_flow"]);
  store.setStatus(
    `Watching for ${FLOW_WINDOW_SECONDS}s. Best run while the tunnel at ` +
      "the site is being restarted."
  );

  const isCurrent = () => token === useVpnDebuggerStore.getState().runToken;

  const finish = (message: string, isError = false) => {
    const current = useVpnDebuggerStore.getState();
    current.setRunning(false);
    current.setStage("");
    current.setPendingSteps([]);
    current.setStatus(message, isError);
  };

  try {
    const job = await new Promise<VpnDebugEnqueueResponse>((resolve, reject) => {
      const AxiosUI = createApi("");
      AxiosUI.post("network-vpn-debug/flow/", {
        tunnel_id: tunnelId,
        tunnel_task_id: tunnelTaskId,
        window_seconds: FLOW_WINDOW_SECONDS,
      }).then(
        (response: AxiosResponse<VpnDebugEnqueueResponse>) =>
          resolve(response.data),
        (reason: AxiosError) => {
          universalAPIErrorHandler(reason, navigate, retryFunction, () => {
            reject(
              new Error(
                extractVpnDebugApiError(
                  reason,
                  "Could not start the packet-flow window."
                )
              )
            );
          });
        }
      );
    });
    if (!isCurrent()) return;

    const result = await pollVpnDebugTask(
      job.task_id,
      token,
      startedAt,
      "Packet flow"
    );
    if (!isCurrent()) return;

    mergeVpnDebugResult(result);
    finish("Packet-flow window complete.");
  } catch (error) {
    if (!isCurrent()) return;
    finish(error instanceof Error ? error.message : String(error), true);
  }
};
